import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { PaywallButton } from "@/components/ui/PaywallButton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useGetQuestionnaireItems } from "@/hooks/api/query/useGetQuestionnaireItems";
import { useSaveMaturityPreliminary } from "@/hooks/api/mutate/useSaveMaturityPreliminary";
import { useFinalizeMaturityPreliminary } from "@/hooks/api/mutate/useFinalizeMaturityPreliminary";
import { useCheckActionCredits } from "@/hooks/api/query/useCheckActionCredits";
import {
  computePreliminaryScores,
  computePreliminaryOverallScore,
} from "@/utils/maturity-scoring";
import { PaywallDialog } from "@/components/PaywallDialog";
import { CREDIT_ACTION_TYPES } from "@/constants/credit";
import { InsufficientCreditsError } from "@/utils/supabaseFunctionsError";
import { PrelimHeader } from "../PrelimHeader";
import { PrelimSection } from "../PrelimSection";
import { SaveIndicator } from "../SaveIndicator";
import {
  SECTION_ORDER,
  type MaturityEvaluation,
  type MaturityFrameworkResponse,
  type PageView,
  type ElementType,
  type SaveStatus,
} from "@/types/maturity";

type PreliminaryViewProps = {
  evaluation: MaturityEvaluation;
  framework: MaturityFrameworkResponse;
  onNavigate: (view: PageView) => void;
};

function PreliminaryView({
  evaluation,
  framework,
  onNavigate,
}: PreliminaryViewProps) {
  const [responses, setResponses] = useState<Record<string, boolean>>(
    () => evaluation.preliminary_responses ?? {}
  );
  const [enabledSections, setEnabledSections] = useState<
    Record<ElementType, boolean>
  >(() => {
    const initial: Record<string, boolean> = {};
    for (const section of SECTION_ORDER) {
      initial[section] = true;
    }
    return initial as Record<ElementType, boolean>;
  });
  const [expandedSections, setExpandedSections] = useState<
    Record<ElementType, boolean>
  >(() => {
    const initial: Record<string, boolean> = {};
    for (const section of SECTION_ORDER) {
      initial[section] = section === SECTION_ORDER[0];
    }
    return initial as Record<ElementType, boolean>;
  });
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");

  const { data: questionnaireData } = useGetQuestionnaireItems(
    evaluation.assurance_level
  );
  const saveMutation = useSaveMaturityPreliminary(evaluation.form_session_id);
  const finalizeMutation = useFinalizeMaturityPreliminary(
    evaluation.form_session_id
  );
  const { hasInsufficientCredits } = useCheckActionCredits(
    {
      session_id: evaluation.form_session_id,
      action_type: CREDIT_ACTION_TYPES.CHESTIONAR_PRELIMINAR_V2,
    },
    !!evaluation.form_session_id
  );

  const items = questionnaireData?.items ?? [];

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const saveMutationRef = useRef(saveMutation);
  useEffect(() => {
    saveMutationRef.current = saveMutation;
  }, [saveMutation]);

  const finalizeMutationRef = useRef(finalizeMutation);
  useEffect(() => {
    finalizeMutationRef.current = finalizeMutation;
  }, [finalizeMutation]);

  const [showPaywall, setShowPaywall] = useState(false);

  const debouncedSave = useCallback((newResponses: Record<string, boolean>) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);

    setSaveStatus("saving");
    timeoutRef.current = setTimeout(() => {
      saveMutationRef.current.mutate(newResponses, {
        onSuccess: () => {
          setSaveStatus("saved");
          savedTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
        },
        onError: () => {
          setSaveStatus("error");
        },
      });
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  const itemsBySection = useMemo(() => {
    const grouped: Record<string, typeof items> = {};
    for (const section of SECTION_ORDER) {
      grouped[section] = [];
    }
    for (const item of items) {
      if (grouped[item.element_type]) {
        grouped[item.element_type].push(item);
      }
    }
    return grouped;
  }, [items]);

  const handleCheckboxChange = useCallback(
    (itemIndex: string, checked: boolean) => {
      setResponses((prev) => {
        const next = { ...prev, [itemIndex]: checked };
        debouncedSave(next);
        return next;
      });
    },
    [debouncedSave]
  );

  const handleToggleAll = useCallback(
    (itemIndexes: string[], checked: boolean) => {
      setResponses((prev) => {
        const next = { ...prev };
        for (const idx of itemIndexes) {
          next[idx] = checked;
        }
        debouncedSave(next);
        return next;
      });
    },
    [debouncedSave]
  );

  const handleSelectAllInSection = useCallback(
    (sectionType: ElementType) => {
      const sectionItems = itemsBySection[sectionType] ?? [];
      const indexes = sectionItems.map((item) => String(item.item_index));
      const allChecked = indexes.every((idx) => responses[idx] === true);
      handleToggleAll(indexes, !allChecked);
    },
    [itemsBySection, handleToggleAll, responses]
  );

  const [pendingDisableSection, setPendingDisableSection] =
    useState<ElementType | null>(null);

  const handleToggleEnabled = useCallback(
    (section: ElementType, enabled: boolean) => {
      if (!enabled) {
        setPendingDisableSection(section);
        return;
      }
      setEnabledSections((prev) => ({ ...prev, [section]: true }));
      setExpandedSections((prev) => ({ ...prev, [section]: true }));
      setTimeout(() => {
        document
          .getElementById(`section-${section}`)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    },
    []
  );

  const confirmDisableSection = useCallback(() => {
    if (!pendingDisableSection) return;
    const section = pendingDisableSection;
    setEnabledSections((prev) => ({ ...prev, [section]: false }));
    const sectionItems = itemsBySection[section] ?? [];
    const indexes = sectionItems.map((item) => String(item.item_index));
    setResponses((prev) => {
      const next = { ...prev };
      for (const idx of indexes) {
        next[idx] = false;
      }
      debouncedSave(next);
      return next;
    });
    setPendingDisableSection(null);
  }, [pendingDisableSection, itemsBySection, debouncedSave]);

  const handleToggleExpanded = useCallback((section: ElementType) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const handleNextSection = useCallback((currentIndex: number) => {
    if (currentIndex < SECTION_ORDER.length - 1) {
      const nextSection = SECTION_ORDER[currentIndex + 1];
      setExpandedSections((prev) => {
        const next = { ...prev };
        next[SECTION_ORDER[currentIndex]] = false;
        next[nextSection] = true;
        return next;
      });

      setTimeout(() => {
        const el = document.getElementById(`section-${nextSection}`);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, []);

  const handleFinalize = useCallback(async () => {
    if (hasInsufficientCredits) {
      setShowPaywall(true);
      return;
    }
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);

    setSaveStatus("saving");
    try {
      await saveMutationRef.current.mutateAsync(responses);
      await finalizeMutationRef.current.mutateAsync(undefined);
      setSaveStatus("saved");
      onNavigate("summary");
    } catch (error) {
      if (error instanceof InsufficientCreditsError) {
        setSaveStatus("idle");
        setShowPaywall(true);
      } else {
        setSaveStatus("error");
      }
    }
  }, [responses, onNavigate, hasInsufficientCredits]);

  const { totalChecked, overallScore, categoryScores, sectionStats } =
    useMemo(() => {
      const sectionCounts: Record<string, { done: number; total: number }> = {};
      for (const section of SECTION_ORDER) {
        sectionCounts[section] = { done: 0, total: 0 };
      }

      let checked = 0;
      for (const item of items) {
        const isChecked = responses[String(item.item_index)] === true;
        if (isChecked) checked++;

        const sec = sectionCounts[item.element_type];
        if (sec) {
          sec.total++;
          if (isChecked) sec.done++;
        }
      }

      return {
        totalChecked: checked,
        overallScore: computePreliminaryOverallScore(responses, items),
        categoryScores: computePreliminaryScores(
          responses,
          items,
          framework.categories
        ),
        sectionStats: SECTION_ORDER.map((type) => ({
          type,
          done: sectionCounts[type]?.done ?? 0,
          total: sectionCounts[type]?.total ?? 0,
        })),
      };
    }, [items, responses, framework.categories]);

  const isFinalizing = finalizeMutation.isPending || saveMutation.isPending;

  return (
    <div className="max-w-[900px] mx-auto space-y-5">
      <div className="flex items-center justify-end">
        <SaveIndicator
          status={saveStatus}
          onRetry={() => debouncedSave(responses)}
        />
      </div>

      <PrelimHeader
        totalChecked={totalChecked}
        totalItems={items.length}
        score={overallScore}
        sectionStats={sectionStats}
        categories={framework.categories}
        categoryScores={categoryScores}
      />

      <div className="rounded-xl border-l-4 border-blue-400 bg-blue-50 p-4">
        <p className="text-sm text-slate-700">
          <strong>Scor preliminar</strong> — plafonat la 2.5. Rezultatul este
          estimativ; pentru un scor complet și conformitate NIS2 continuați cu
          evaluarea completă.
        </p>
      </div>

      <div className="space-y-4">
        {SECTION_ORDER.map((sectionType, index) => (
          <div key={sectionType} id={`section-${sectionType}`}>
            <PrelimSection
              sectionType={sectionType}
              items={itemsBySection[sectionType] ?? []}
              responses={responses}
              enabled={enabledSections[sectionType]}
              expanded={expandedSections[sectionType]}
              onToggleEnabled={(enabled) =>
                handleToggleEnabled(sectionType, enabled)
              }
              onToggleExpanded={() => handleToggleExpanded(sectionType)}
              onCheckboxChange={handleCheckboxChange}
              onToggleAll={handleToggleAll}
              onSelectAll={() => handleSelectAllInSection(sectionType)}
              onNextSection={
                index < SECTION_ORDER.length - 1
                  ? () => handleNextSection(index)
                  : handleFinalize
              }
              isLastSection={index === SECTION_ORDER.length - 1}
              activities={framework.activities}
            />
          </div>
        ))}
      </div>

      <div className="flex justify-center pb-8">
        <PaywallButton
          locked={!isFinalizing && hasInsufficientCredits}
          size="lg"
          className="gap-2"
          disabled={isFinalizing || totalChecked === 0}
          onClick={handleFinalize}
        >
          {isFinalizing ? "Se finalizează..." : "Continuă"}
        </PaywallButton>
      </div>

      <AlertDialog
        open={pendingDisableSection !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDisableSection(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Dezactivezi secțiunea „{pendingDisableSection}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Toate răspunsurile din această secțiune vor fi șterse. Poți
              reactiva secțiunea oricând.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDisableSection}>
              Dezactivează
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <PaywallDialog
        open={showPaywall}
        onOpenChange={setShowPaywall}
        actionType={CREDIT_ACTION_TYPES.CHESTIONAR_PRELIMINAR_V2}
      />
    </div>
  );
}

export default PreliminaryView;
